import React, { FC, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import DrawerMenu from 'components/DrawerMenu';
import DrawerContent from './DrawerContent';
import RankBadge from './RankBadge';
import { formatCompactIndianCurrency, formatNumber } from '../utils/formatters';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';
import axios from 'axios';
import Config from 'utils/Config';
import { URLS } from 'utils/constants/urls';
import { downloadExportFile } from 'utils/validators/HelperFunctions';
import { fetchFromStorage } from 'utils/storage';
import { identifiers } from 'utils/constants/identifiers';

const TABLE_CELL_PADDING = { py: 1.25, px: 1.5 };
const HEADER_CELL_STYLES = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
  ...TABLE_CELL_PADDING,
};

const VIRTUAL_ACCOUNT_ID = -1;
const TOP_MANAGERS_LIMIT = 5;

const METRIC_DEFS: Array<{ key: string; label: string; currency: boolean }> = [
  { key: 'miceCount', label: 'Event Count', currency: false },
  { key: 'miceRevenue', label: 'Event Revenue', currency: true },
  { key: 'roomNight', label: 'Room Count', currency: false },
  { key: 'roomRevenue', label: 'Room Revenue', currency: true },
];

const sumRevenue = (summary: any): number =>
  Number(summary?.roomRevenue?.achieved ?? 0) + Number(summary?.miceRevenue?.achieved ?? 0);

const sumTarget = (summary: any): number =>
  Number(summary?.roomRevenue?.target ?? 0) + Number(summary?.miceRevenue?.target ?? 0);

const toPercent = (actual: number, target: number): number | null =>
  target > 0 ? (actual / target) * 100 : null;

// Cap the displayed achievement so extreme values render as "100%+".
const formatPercentLabel = (percentage: number | null): string => {
  if (percentage === null) return '—';
  if (percentage > 100) return '100%+';
  return `${Math.round(percentage)}%`;
};

const progressColor = (percentage: number | null): string => {
  if (percentage === null) return '#CBD5E1';
  if (percentage >= 100) return '#059669';
  if (percentage >= 80) return '#D97706';
  return '#DC2626';
};

const countCompanies = (manager: any): number =>
  (Array.isArray(manager?.accounts) ? manager.accounts : []).filter(
    (account: any) => account?.accountId !== VIRTUAL_ACCOUNT_ID,
  ).length;

interface AchievementBarProps {
  revenue: number;
  target: number;
  height?: number;
}

const AchievementBar: FC<AchievementBarProps> = ({ revenue, target, height = 8 }) => {
  const percentage = toPercent(revenue, target);
  const color = progressColor(percentage);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, minWidth: 150 }}>
      <Box
        sx={{
          flexGrow: 1,
          maxWidth: 120,
          height,
          borderRadius: 999,
          backgroundColor: '#EEF2F6',
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            width: `${Math.min(percentage ?? 0, 100)}%`,
            height: '100%',
            backgroundColor: color,
            borderRadius: 999,
            transition: 'width 0.3s ease',
          }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color, fontSize: '0.75rem', minWidth: 44, textAlign: 'right' }}>
        {formatPercentLabel(percentage)}
      </Typography>
    </Box>
  );
};

interface ManagerWisePerformanceProps {
  managerWisePerformance: any[];
  managerWisePerformanceLoading: boolean;
  managerWisePeriodType: string;
  managerWisePeriod: string | number;
  getInitials: (name: string) => string;
}

const ManagerWisePerformance: FC<ManagerWisePerformanceProps> = ({
  managerWisePerformance,
  managerWisePerformanceLoading,
  managerWisePeriodType,
  managerWisePeriod,
  getInitials,
}) => {
  const [breakupManager, setBreakupManager] = useState<any | null>(null);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string | number>>(new Set());
  const [viewAllOpen, setViewAllOpen] = useState(false);

  const currencySymbol = getCurrencySymbol();
  const formatMoney = (value: number) => formatCompactIndianCurrency(value, currencySymbol);

  const rankedManagers = useMemo(() => {
    return (Array.isArray(managerWisePerformance) ? [...managerWisePerformance] : [])
      .map(manager => {
        const revenue = sumRevenue(manager?.summary);
        const target = sumTarget(manager?.summary);
        return {
          ...manager,
          totalCompanies: countCompanies(manager),
          revenue,
          target,
        };
      })
      .sort((a, b) => b.revenue - a.revenue)
      .map((manager, index) => ({ ...manager, rank: index + 1 }));
  }, [managerWisePerformance]);

  const topManagers = rankedManagers.slice(0, TOP_MANAGERS_LIMIT);

  const openBreakup = (manager: any) => {
    setExpandedAccounts(new Set());
    setBreakupManager(manager);
  };

  const toggleAccount = (accountKey: string | number) => {
    setExpandedAccounts(prev => {
      const next = new Set(prev);
      if (next.has(accountKey)) {
        next.delete(accountKey);
      } else {
        next.add(accountKey);
      }
      return next;
    });
  };

  const handleExport = () => {
    if (!managerWisePeriod || !managerWisePeriodType || managerWisePerformance?.length === 0) {
      return;
    }
    const token = fetchFromStorage(identifiers.access_token) as any;
    axios({
      url: `${Config.BaseURL + URLS.exportManagersPerformance(Number(managerWisePeriod))}`,
      method: 'POST',
      data: {},
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    })
      .then(response => {
        downloadExportFile(response, 'xlsx', 'Manager_Wise_Performance.xlsx');
      })
      .catch((err: any) => {
        alert(err.message);
      });
  };

  const renderPerformanceTable = (managers: any[]) => (
    <Box sx={{ overflowX: 'auto' }}>
      <Table sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
            <TableCell sx={{ ...HEADER_CELL_STYLES, width: 56 }} align="center">
              #
            </TableCell>
            <TableCell sx={HEADER_CELL_STYLES}>Manager</TableCell>
            <TableCell sx={HEADER_CELL_STYLES} align="center">
              Companies
            </TableCell>
            <TableCell sx={HEADER_CELL_STYLES} align="right">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                Revenue
                <Tooltip title="Achieved revenue for the period = Room Revenue + Event Revenue." arrow>
                  <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
              </Box>
            </TableCell>
            <TableCell sx={HEADER_CELL_STYLES} align="right">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                Target
                <Tooltip title="Target revenue for the period = Room Revenue Target + Event Revenue Target." arrow>
                  <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
              </Box>
            </TableCell>
            <TableCell sx={{ ...HEADER_CELL_STYLES, minWidth: 160 }} align="right">
              Achievement
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {managers.map(manager => (
            <TableRow
              key={manager.userId}
              hover
              sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#F9FAFB' } }}
              onClick={() => openBreakup(manager)}>
              <TableCell sx={TABLE_CELL_PADDING} align="center">
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <RankBadge rank={manager.rank} />
                </Box>
              </TableCell>
              <TableCell sx={TABLE_CELL_PADDING}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: '#EEF2FF',
                      color: '#4338CA',
                      fontSize: '0.8125rem',
                      fontWeight: 700,
                    }}>
                    {getInitials(manager.userName)}
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {manager.userName}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={TABLE_CELL_PADDING} align="center">
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {formatNumber(manager.totalCompanies)}
                </Typography>
              </TableCell>
              <TableCell sx={TABLE_CELL_PADDING} align="right">
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {formatMoney(manager.revenue)}
                </Typography>
              </TableCell>
              <TableCell sx={TABLE_CELL_PADDING} align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {formatMoney(manager.target)}
                </Typography>
              </TableCell>
              <TableCell sx={TABLE_CELL_PADDING} align="right">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <AchievementBar revenue={manager.revenue} target={manager.target} />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  // Single metric with actual vs target and progress bar.
  const MetricProgress: FC<{ label: string; metric: any; currency: boolean; hideTarget?: boolean }> = ({
    label,
    metric,
    currency,
    hideTarget = false,
  }) => {
    const actual = Number(metric?.achieved ?? 0);
    const target = Number(metric?.target ?? 0);
    const percentage = toPercent(actual, target);
    const color = progressColor(percentage);
    const fmt = currency ? formatMoney : (v: number) => formatNumber(v);

    if (hideTarget) {
      return (
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.25 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {fmt(actual)}
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color, fontSize: '0.72rem' }}>
            {percentage === null ? 'No target' : formatPercentLabel(percentage)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {fmt(actual)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            / {fmt(target)}
          </Typography>
        </Box>
        <Box sx={{ height: 6, borderRadius: 999, backgroundColor: '#EEF2F6', overflow: 'hidden' }}>
          <Box
            sx={{
              width: `${Math.min(percentage ?? 0, 100)}%`,
              height: '100%',
              backgroundColor: color,
              borderRadius: 999,
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
      </Box>
    );
  };

  const MetricGroup: FC<{ summary: any; hideTarget?: boolean }> = ({ summary, hideTarget = false }) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        gap: 2,
      }}>
      {METRIC_DEFS.map(def => (
        <MetricProgress
          key={def.key}
          label={def.label}
          metric={summary?.[def.key]}
          currency={def.currency}
          hideTarget={hideTarget}
        />
      ))}
    </Box>
  );

  const breakupAccounts: any[] = Array.isArray(breakupManager?.accounts) ? breakupManager.accounts : [];
  const individualAccount = breakupAccounts.find((a: any) => a?.accountId === VIRTUAL_ACCOUNT_ID);
  const companyAccounts = breakupAccounts.filter((a: any) => a?.accountId !== VIRTUAL_ACCOUNT_ID);

  const showEmptyState =
    !managerWisePerformanceLoading &&
    managerWisePeriod &&
    managerWisePeriodType &&
    rankedManagers.length === 0;

  const showData =
    !managerWisePerformanceLoading &&
    managerWisePeriod &&
    managerWisePeriodType &&
    rankedManagers.length > 0;

  return (
    <Box sx={{ '&:hover .mw-export-btn': { opacity: 1, pointerEvents: 'auto' } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Manager Performance
          </Typography>
          <Chip
            label={`${rankedManagers.length} managers`}
            variant="outlined"
            size="small"
            sx={{ height: 22, borderColor: 'primary.main', color: 'primary.main', fontWeight: 600 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', ml: 'auto' }}>
          <Tooltip title="Export" arrow>
            <IconButton
              className="mw-export-btn"
              onClick={handleExport}
              size="small"
              sx={{
                borderRadius: '50%',
                color: 'text.secondary',
                backgroundColor: '#F1F5F9',
                p: 0.75,
                flexShrink: 0,
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.2s ease, background-color 0.2s ease',
                '&:hover': { backgroundColor: '#E2E8F0', color: 'text.primary' },
              }}>
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {managerWisePerformanceLoading && (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading manager performance…
          </Typography>
        </Box>
      )}

      {!managerWisePerformanceLoading && (!managerWisePeriod || !managerWisePeriodType) && (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Please select period type and period to view performance.
          </Typography>
        </Box>
      )}

      {showEmptyState && (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No data available for the selected period.
          </Typography>
        </Box>
      )}

      {showData && (
        <Box sx={{ mt: 1 }}>
          {renderPerformanceTable(topManagers)}
          {rankedManagers.length > TOP_MANAGERS_LIMIT && (
            <Box sx={{ mt: 1.5, textAlign: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setViewAllOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 600 }}>
                View all managers ({rankedManagers.length})
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* View all managers drawer */}
      <DrawerMenu
        width="70%"
        open={viewAllOpen}
        handleClose={() => setViewAllOpen(false)}
        closeButton
        title="All managers — performance"
        dropBack>
        <DrawerContent padding={3}>{renderPerformanceTable(rankedManagers)}</DrawerContent>
      </DrawerMenu>

      {/* Breakup drawer */}
      <DrawerMenu
        width="70%"
        open={Boolean(breakupManager)}
        handleClose={() => setBreakupManager(null)}
        closeButton
        title={breakupManager ? `${breakupManager.userName} — Performance breakup` : ''}
        dropBack>
        <DrawerContent padding={3}>
          {breakupManager && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Overall */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: '#F8FAFC',
                  border: '1px solid',
                  borderColor: 'divider',
                }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Overall
                  </Typography>
                  <Chip
                    label={`${formatNumber(breakupManager.totalCompanies)} companies`}
                    size="small"
                    sx={{ backgroundColor: '#EEF2FF', color: '#4338CA', fontWeight: 600 }}
                  />
                </Box>
                <MetricGroup summary={breakupManager.summary} />
              </Box>

              {/* Individual */}
              {individualAccount && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PersonIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Individual
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                      {individualAccount.accountName || 'Individual'}
                    </Typography>
                    <MetricGroup summary={individualAccount.summary} hideTarget />
                  </Box>
                </Box>
              )}

              {/* Companies */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <BusinessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Companies
                  </Typography>
                </Box>

                {companyAccounts.length === 0 ? (
                  <Box sx={{ py: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No company data available for this manager.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {companyAccounts.map((account: any) => {
                      const accountKey = `${account?.accountId}`;
                      const branches: any[] = Array.isArray(account?.branches) ? account.branches : [];
                      const hasBranches = branches.length > 0;
                      const isExpanded = expandedAccounts.has(accountKey);

                      return (
                        <Box
                          key={accountKey}
                          sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                          <Box
                            sx={{
                              p: 2,
                              cursor: hasBranches ? 'pointer' : 'default',
                              backgroundColor: isExpanded ? '#F8FAFC' : '#fff',
                            }}
                            onClick={hasBranches ? () => toggleAccount(accountKey) : undefined}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              {hasBranches ? (
                                <IconButton
                                  size="small"
                                  onClick={e => {
                                    e.stopPropagation();
                                    toggleAccount(accountKey);
                                  }}>
                                  {isExpanded ? (
                                    <ExpandLessIcon fontSize="small" />
                                  ) : (
                                    <ExpandMoreIcon fontSize="small" />
                                  )}
                                </IconButton>
                              ) : (
                                <Box sx={{ width: 34 }} />
                              )}
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {account?.accountName}
                              </Typography>
                              {account?.category && (
                                <Chip
                                  label={`Category ${account.category}`}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.68rem', backgroundColor: '#F1F5F9' }}
                                />
                              )}
                              {hasBranches && (
                                <Chip
                                  label={`${branches.length} branch${branches.length > 1 ? 'es' : ''}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.68rem', ml: 'auto' }}
                                />
                              )}
                            </Box>
                            <Box sx={{ pl: { xs: 0, sm: 4.5 } }}>
                              <MetricGroup summary={account?.summary} />
                            </Box>
                          </Box>

                          {hasBranches && (
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ backgroundColor: '#FCFCFD', borderTop: '1px solid', borderColor: 'divider' }}>
                                {branches.map((branch: any) => (
                                  <Box
                                    key={branch?.branchId}
                                    sx={{
                                      p: 2,
                                      pl: { xs: 2, sm: 5 },
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                      '&:last-child': { borderBottom: 'none' },
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {branch?.branchName}
                                      </Typography>
                                      {branch?.city && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                          {branch.city}
                                        </Typography>
                                      )}
                                    </Box>
                                    <MetricGroup summary={branch?.summary} />
                                  </Box>
                                ))}
                              </Box>
                            </Collapse>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DrawerContent>
      </DrawerMenu>
    </Box>
  );
};

export default ManagerWisePerformance;
